import { FormEvent, useContext, useEffect, useMemo, useState, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Checkbox, DefaultButton, Dialog, FontIcon, Stack, Text } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { ThumbDislike20Filled, ThumbLike20Filled, CopyRegular, DocumentRegular } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import { AskResponse, Citation, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'

import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'

interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onExectResultClicked: (answerId: string) => void
}

export const Answer = ({ answer, onCitationClicked, onExectResultClicked }: Props) => {
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (answer.feedback.split(',').length > 1) return Feedback.Negative
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback
    return Feedback.Neutral
  }

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED =
    appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const ui = appStateContext?.state.frontendSettings?.ui
  const [copyClicked, setCopyClicked] = useState<boolean>(false);
  const [exportClicked, setExportClicked] = useState<boolean>(false);

  // New ref to capture the rendered answer content
  const answerContentRef = useRef<HTMLDivElement>(null);

  const handleCopyClick = async () => {
    if (answerContentRef.current) {
      const htmlData = answerContentRef.current.innerHTML;
      const plainText = answerContentRef.current.innerText;
      if (navigator.clipboard && navigator.clipboard.write) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([htmlData], { type: "text/html" }),
              "text/plain": new Blob([plainText], { type: "text/plain" }),
            }),
          ]);
          setCopyClicked(true);
          setTimeout(() => setCopyClicked(false), 2000);
        } catch (err) {
          console.error("Clipboard write failed", err);
          // fallback copy as plain text
          await navigator.clipboard.writeText(plainText);
          setCopyClicked(true);
          setTimeout(() => setCopyClicked(false), 2000);
        }
      } else {
        // Fallback for older browsers
        await navigator.clipboard.writeText(plainText);
        setCopyClicked(true);
        setTimeout(() => setCopyClicked(false), 2000);
      }
    }
  };

  // Function to export answer to Word document
  const handleExportToWord = async () => {
    if (answerContentRef.current) {
      try {
        // Get the HTML content instead of plain text to preserve formatting
        let contentHtml = answerContentRef.current.innerHTML;
        
        // Process the HTML content to ensure proper formatting in Word
        // Fix code blocks to preserve syntax highlighting
        contentHtml = contentHtml
          // Ensure code blocks have proper styling
          .replace(/<pre(.*?)>/g, '<pre style="background-color:#f6f8fa;border:1pt solid #e1e4e8;padding:10pt;font-family:\'Courier New\',monospace;font-size:11pt;">')
          // Fix syntax highlighting spans
          .replace(/<span style="color:(.*?)">/g, '<span style="color:$1;font-family:\'Courier New\',monospace;">')
          // Ensure tables are properly formatted
          .replace(/<table>/g, '<table class="msoPrettyTable" border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;border:1pt solid #d0d0d0;">')
          // Ensure list items are properly formatted
          .replace(/<li>/g, '<li style="margin-bottom:4pt;">');
        
        // Create a title
        const title = "AI Assistant Response";
        const dateText = new Date().toLocaleString();
        
        // Create HTML document with proper Word formatting
        // Using mso (Microsoft Office) namespaces and styles for better Word compatibility
        let htmlContent = `
        <html xmlns:v="urn:schemas-microsoft-com:vml"
              xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns:m="http://schemas.microsoft.com/office/2004/12/omml"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
          <title>${title}</title>
          <meta name="ProgId" content="Word.Document">
          <meta name="Generator" content="Microsoft Word 15">
          <meta name="Originator" content="Microsoft Word 15">
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            /* Default styles */
            body, div, p, h1, h2, h3, h4, h5, h6, ul, ol, li, pre, code {
              margin: 0;
              padding: 0;
              border: 0;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 12pt;
              line-height: 1.5;
              color: #252525;
              margin: 2em;
            }
            h1 {
              font-size: 18pt;
              font-weight: bold;
              margin-bottom: 12pt;
              color: #115EA3;
            }
            .header {
              margin-bottom: 20pt;
            }
            .date {
              font-size: 10pt;
              font-style: italic;
              color: #666666;
              margin-bottom: 16pt;
            }
            .content {
              margin-top: 12pt;
              margin-bottom: 24pt;
            }
            
            /* Code formatting */
            pre, code {
              font-family: 'Courier New', monospace;
              background-color: #f6f8fa;
              border: 1px solid #e1e4e8;
              border-radius: 6px;
              padding: 12pt;
              margin: 10pt 0;
              white-space: pre-wrap;
              overflow-wrap: break-word;
              font-size: 11pt;
            }
            
            /* List formatting */
            ul, ol {
              margin-left: 20pt;
              margin-bottom: 10pt;
            }
            li {
              margin-bottom: 4pt;
            }
            
            /* Citations */
            .citations {
              margin-top: 24pt;
              border-top: 1pt solid #e1e4e8;
              padding-top: 12pt;
            }
            .citation-title {
              font-weight: bold;
              font-size: 14pt;
              margin-bottom: 10pt;
              color: #115EA3;
            }
            .citation-item {
              margin-left: 20pt;
              margin-bottom: 6pt;
            }
            .citation-content {
              margin-left: 40pt;
              margin-bottom: 10pt;
              color: #666666;
              font-style: italic;
              border-left: 2pt solid #e1e4e8;
              padding-left: 10pt;
            }
            
            /* Table styles */
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 10pt 0;
            }
            th {
              background-color: #f1f1f1;
              border: 1pt solid #d0d0d0;
              padding: 8pt;
              font-weight: bold;
              text-align: left;
            }
            td {
              border: 1pt solid #d0d0d0;
              padding: 8pt;
              vertical-align: top;
            }
            
            /* MSO specific styles for better Word compatibility */
            /* Word-specific properties */
            @page {
              size: 8.5in 11in;
              margin: 1in;
            }
            .msoPrettyTable {
              border-collapse: collapse;
              border: 1pt solid windowtext;
              mso-border-alt: solid windowtext .5pt;
              mso-padding-alt: 0in 5.4pt 0in 5.4pt;
            }
            
            /* Additional styles for syntax highlighting */
            .hljs-keyword {
              color: #0000FF;
              font-weight: bold;
            }
            .hljs-string {
              color: #A31515;
            }
            .hljs-comment {
              color: #008000;
            }
            .hljs-number {
              color: #098658;
            }
            .hljs-function {
              color: #795E26;
            }
            .hljs-title {
              color: #795E26;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            <div class="date">${dateText}</div>
          </div>
          <div class="content">
        `;
        
        // Insert the HTML content directly to preserve formatting
        htmlContent += contentHtml;
        
        htmlContent += `
          </div>
        `;
        
        // Add citations if available
        if (parsedAnswer?.citations && parsedAnswer.citations.length > 0) {
          htmlContent += `<div class="citations">
            <div class="citation-title">References</div>
          `;
          
          parsedAnswer.citations.forEach((citation, index) => {
            const citationTitle = citation.title || 'Untitled';
            const citationPath = citation.filepath ? ` (${citation.filepath})` : '';
            
            htmlContent += `
              <div class="citation-item">${index + 1}. ${citationTitle}${citationPath}</div>
            `;
            
            if (citation.content) {
              // Clean the citation content and apply styling
              const sanitizedContent = DOMPurify.sanitize(citation.content)
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
                
              htmlContent += `<div class="citation-content">${sanitizedContent}</div>`;
            }
          });
          
          htmlContent += `</div>`;
        }
        
        // Close HTML document
        htmlContent += `</body></html>`;
        
        // Create Blob with MIME HTML type for better Word compatibility
        const blob = new Blob([htmlContent], { 
          type: 'application/x-mimearchive' 
        });
        const url = URL.createObjectURL(blob);
        
        // Create download link with .mht extension which Word can open natively
        const link = document.createElement('a');
        link.href = url;
        
        // Ensure file has .mht extension
        let filename = 'ai-assistant-response';
        if (!filename.endsWith('.mht')) {
          filename = filename + '.mht';
        }
        link.download = filename;
        
        console.log('Downloading file with name:', link.download); // Add logging to verify filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
        
        // Provide feedback to user
        setExportClicked(true);
        setTimeout(() => setExportClicked(false), 2000);
      } catch (error) {
        console.error("Error exporting to Word:", error);
      }
    }
  };

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }
    return citationFilename
  }

  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, newFeedbackState)
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, Feedback.Neutral)
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    await historyMessageFeedback(answer.message_id, negativeFeedbackList.join(','))
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
  }

  const UnhelpfulFeedbackContent = () => {
    return (
      <>
        <div>Why wasn't this response helpful?</div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Citations are missing"
            id={Feedback.MissingCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.MissingCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Citations are wrong"
            id={Feedback.WrongCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.WrongCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="The response is not from my data"
            id={Feedback.OutOfScope}
            defaultChecked={negativeFeedbackList.includes(Feedback.OutOfScope)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Inaccurate or irrelevant"
            id={Feedback.InaccurateOrIrrelevant}
            defaultChecked={negativeFeedbackList.includes(Feedback.InaccurateOrIrrelevant)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherUnhelpful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
        <div onClick={() => setShowReportInappropriateFeedback(true)} style={{ color: '#115EA3', cursor: 'pointer' }}>
          Report inappropriate content
        </div>
      </>
    )
  }

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }

  const components = {
    code({ node, ...props }: { node: any;[key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }
  return (
    <>
      <Stack className={styles.answerContainer} tabIndex={0}>
        <Stack.Item>
          <Stack horizontal grow>
            <Stack.Item grow>
              {parsedAnswer && (
                <div ref={answerContentRef}>
                  <ReactMarkdown
                    linkTarget="_blank"
                    remarkPlugins={[remarkGfm, supersub]}
                    children={
                      SANITIZE_ANSWER
                        ? DOMPurify.sanitize(parsedAnswer?.markdownFormatText, { ALLOWED_TAGS: XSSAllowTags, ALLOWED_ATTR: XSSAllowAttributes })
                        : parsedAnswer?.markdownFormatText
                    }
                    className={styles.answerText}
                    components={components}
                  />
                </div>
              )}
            </Stack.Item>
            <Stack.Item className={styles.answerHeader}>
              {FEEDBACK_ENABLED && answer.message_id !== undefined && (
                <Stack horizontal horizontalAlign="space-between" styles={{
                  root: {
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    marginBottom: '12px'
                  }
                }}>
                  <ThumbLike20Filled
                    aria-hidden="false"
                    aria-label="Like this response"
                    onClick={() => onLikeResponseClicked()}
                    style={
                      feedbackState === Feedback.Positive ||
                        appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive
                        ? { color: 'darkgreen', cursor: 'pointer' }
                        : { color: 'slategray', cursor: 'pointer' }
                    }
                  />
                  <ThumbDislike20Filled
                    aria-hidden="false"
                    aria-label="Dislike this response"
                    onClick={() => onDislikeResponseClicked()}
                    style={
                      feedbackState !== Feedback.Positive &&
                        feedbackState !== Feedback.Neutral &&
                        feedbackState !== undefined
                        ? { color: 'darkred', cursor: 'pointer' }
                        : { color: 'slategray', cursor: 'pointer' }
                    }
                  />
                </Stack>
              )}
            </Stack.Item>
          </Stack>
        </Stack.Item>
        {parsedAnswer?.generated_chart !== null && (
          <Stack className={styles.answerContainer}>
            <Stack.Item grow>
              <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} alt="Generated chart" />
            </Stack.Item>
          </Stack>
        )}
        <Stack horizontal className={styles.answerFooter}>
          {!!parsedAnswer?.citations.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack style={{ width: '100%' }}>
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={toggleIsRefAccordionOpen}
                    aria-label="Open references"
                    tabIndex={0}
                    role="button">
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + ' references'
                        : '1 reference'}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
          <Stack.Item className={styles.answerDisclaimerContainer}>
            <span className={styles.answerDisclaimer}>AI-generated content may be incorrect</span>
          </Stack.Item>
          <button 
            className={styles.copyButton} 
            onClick={handleCopyClick}
            aria-label={copyClicked ? "Copied to clipboard" : "Copy to clipboard"}
          >
            <CopyRegular aria-hidden="true" /> {copyClicked ? "Copied!" : "Copy"}
          </button>
          {ui?.show_word_doc_button && <button 
            className={styles.copyButton} 
            onClick={handleExportToWord}
            aria-label={exportClicked ? "Exported to Word (.mht)" : "Export to Word (.mht)"}
          >
            <DocumentRegular aria-hidden="true" /> {exportClicked ? "Exported!" : "Export to Word (.mht)"}
          </button>}
          {!!answer.exec_results?.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack style={{ width: '100%' }}>
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={() => onExectResultClicked(answer.message_id ?? '')}
                    aria-label="Show Intents"
                    aria-expanded={chevronIsExpanded}
                    tabIndex={0}
                    role="button">
                    <span>
                      Show Intents
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={'ChevronRight'}
                    aria-hidden="true"
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div className={styles.citationWrapper}>
            {parsedAnswer?.citations.map((citation, idx) => {
              const citationPath = createCitationFilepath(citation, ++idx);
              console.log('Citation data:', {
                citation,
                citationPath,
                hasUrl: !!citation.url,
                hasFilepath: !!citation.filepath,
                url: citation.url,
                filepath: citation.filepath
              });
              return (
                <div className={styles.citationContainer} key={idx}>
                  <span
                    title={citationPath}
                    tabIndex={0}
                    role="link"
                    onClick={() => {
                      if (ui?.show_citation_panel) {
                        onCitationClicked(citation);
                      }
                    }}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null)}
                    aria-label={citationPath}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className={styles.citation}>{idx}</div>
                    {createCitationFilepath(citation, idx, true)}
                  </span>
                  {appStateContext?.state.frontendSettings?.storage?.container_name && 
                  <DefaultButton
                    className={styles.viewDocumentButton}
                    onClick={() => {
                      // Get container name from frontend settings
                      const containerName = appStateContext?.state.frontendSettings?.storage?.container_name || 'documents';
                      
                      // Always use the filepath with our document API endpoint
                      const documentPath = citation.filepath || (citation.url && citation.url.includes('blob.core') ? 
                        citation.url.includes(`/${containerName}/`) ? citation.url.split(`/${containerName}/`)[1] : null : null);
                        
                      if (documentPath) {
                        const viewerUrl = `/api/document?path=${encodeURIComponent(documentPath)}`;
                        console.log('Opening document:', viewerUrl);
                        window.open(viewerUrl, '_blank');
                      } else if (citation.url && !citation.url.includes('blob.core')) {
                        // For non-blob URLs, open directly
                        window.open(citation.url, '_blank');
                      } else {
                        console.error('No valid document path or URL found:', citation);
                      }
                    }}
                    styles={{
                      root: {
                        minWidth: '120px',
                        height: '32px',
                        padding: '0 12px',
                        background: '#f3f3f3',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#333',
                        marginLeft: '8px'
                      },
                      rootHovered: {
                        background: '#e6e6e6',
                        borderColor: '#c0c0c0'
                      }
                    }}
                  >
                    View Document
                  </DefaultButton>}
                </div>
              )
            })}
          </div>
        )}
      </Stack>
      <Dialog
        onDismiss={() => {
          resetFeedbackDialog()
          setFeedbackState(Feedback.Neutral)
        }}
        hidden={!isFeedbackDialogOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '600px',
                  minHeight: '100px'
                }
              }
            }
          ]
        }}
        dialogContentProps={{
          title: 'Submit Feedback',
          showCloseButton: true
        }}>
        <Stack tokens={{ childrenGap: 4 }}>
          <div>Your feedback will improve this experience.</div>

          {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}

          <div>By pressing submit, your feedback will be visible to the application owner.</div>

          <DefaultButton disabled={negativeFeedbackList.length < 1} onClick={onSubmitNegativeFeedback}>
            Submit
          </DefaultButton>
        </Stack>
      </Dialog>
    </>
  )
}
